import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { Department } from "@enums/department.enum";
import { Shift } from "@enums/shift.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ChangeUserProfileDepartment } from "@interfaces/dto/changeUserProfileDepartment.interface";
import { DownloadWorkforceReport } from "@interfaces/downloadWorkforceReport.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { Site } from "@interfaces/site.interface";
import { ExportLogService } from "@services/exportLog.service";
import { MachineService } from "@services/machine.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { SiteService } from "@services/site.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "workforce",
	styleUrls: ["workforce.css"],
	templateUrl: "workforce.html"
})
export class OpsPlanningWorkforceAdminComponent {
	canExportGrid = false;
	departments: any;
	dropdownDepartments: any;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	items: any[] = [];
	minDate: Date = new Date();
	popupAddButtonOptions: any;
	popupChangeUserProfileDepartment: ChangeUserProfileDepartment = new ChangeUserProfileDepartment();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupDownloadWorkforceReport: DownloadWorkforceReport = new DownloadWorkforceReport();
	popupTitle = "Change Current Department";
	popupVisible = false;
	shifts: any;
	sites: Site[] = [];
	title: string;
	
	constructor(private router: Router, private toastService: ToastService, private exportLogService: ExportLogService, private itemService: UserProfileService, private machineService: MachineService, private protectedGridService: ProtectedGridService, private siteService: SiteService) {
		this.departments = GetPropertiesFromEnum(Department);
		this.shifts = GetPropertiesFromEnum(Shift);
		this.title = "Workforce";
		this.changeCurrentDepartmentPopup = this.changeCurrentDepartmentPopup.bind(this);
		this.resetCurrentDepartment = this.resetCurrentDepartment.bind(this);
		this.getItems();
		this.getSites();

		// set allowed departments for the change department dropdown
		this.dropdownDepartments = [];

		const filteredDepartments = this.departments.filter((d: { id: number; name: string; }) => (d.id > 0 && d.id < 55) || d.id == 70);
		filteredDepartments.forEach((fd: any) => {
			this.dropdownDepartments.push(fd);
		});
		this.checkGridExportPermissions();
	}

	buildButtons() {
		this.headerTertiaryButtons = [
			{ method: "goToFirewall", text: "Firewall" },
			{ method: "goToManForMan", text: "Operator Plan" },
			{ method: "goToWorkforce", text: "Workforce" },
			{ method: "goToOutsideProcessMovements", text: "OPM" },
			{ icon: "fal fa-download", method: "downloadWorkforceReportPopup", text: "Report" }
		];

		this.headerSecondaryButtons = [];

		this.dropdownDepartments.forEach((fd: any) => {
			this.headerSecondaryButtons.push({ method: "goToDepartment-" + fd.id, text: fd.name });
		});

		this.headerPrimaryButtons = [];

		this.sites.forEach((site: any) => {
			this.headerPrimaryButtons.push({ method: "goToSite-" + site.id, text: site.name });
		});
	}

	changeCurrentDepartment() {
		this.itemService.changeCurrentDepartment(this.popupChangeUserProfileDepartment)
			.subscribe(
				() => {
					this.toastService.success("Current Department Updated");
					this.getItems();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeCurrentDepartmentPopup(e: any) {
		this.popupChangeUserProfileDepartment = new ChangeUserProfileDepartment();
		this.popupChangeUserProfileDepartment.userProfileId = e.row.data.id;
		this.popupChangeUserProfileDepartment.department = e.row.data.currentDepartment;
		this.popupAddButtonOptions = { onClick: () => this.changeCurrentDepartment(), text: "Change Department" };
		this.popupTitle = "Change Current Department";
		this.popupVisible = true;
	}
	
	checkGridExportPermissions() {
		this.protectedGridService.canExport("Workforce")
			.subscribe(
				(res: any) => {
					this.canExportGrid = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	closePopup() {
		this.popupVisible = false;
	}

	downloadWorkforceReportPopup() {
		this.popupDownloadWorkforceReport = new DownloadWorkforceReport();
		this.popupAddButtonOptions = { onClick: () => this.generateAndDownloadWorkforceReportPDF(), text: "Download" };
		this.popupTitle = "Download Workforce Report";
		this.popupVisible = true;
	}

	generateAndDownloadWorkforceReportPDF() {
		this.machineService.downloadWorkforceReport(this.popupDownloadWorkforceReport).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "WorkforceReport.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
				this.closePopup();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			},
		);
	}

	getItems() {
		this.itemService.getWorkforce()
			.subscribe(
				(res: any) => this.items = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
					this.buildButtons();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	goToDepartment(department: string) {
		this.router.navigate([`/admin/opsPlanning/department/${department}`]);
	}

	goToFirewall() {
		this.router.navigate(["/admin/opsPlanning/firewall"]);
	}

	goToManForMan() {
		this.router.navigate(["/admin/opsPlanning/operatorPlan"]);
	}

	goToOutsideProcessMovements() {
		this.router.navigate(["/admin/opsPlanning/outsideProcessContractMovements"]);
	}

	goToSite(site: number) {
		this.router.navigate([`/admin/opsPlanning/site/${site}`]);
	}

	goToWorkforce() {
		this.router.navigate(["/admin/opsPlanning/workforce"]);
	}

	headerButtonClick(method: string) {
		if (method.indexOf("-") > 0) {
			const split = method.split("-");
			// @ts-ignore // Required to be able to call the method directly from the variable
			this[split[0]](split[1]);
		}
		else
		{
			// @ts-ignore // Required to be able to call the method directly from the variable
			if (this[method]) this[method]();
		}
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "Workforce";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					return false;
				},
			);
	}

	resetCurrentDepartment(e: any) {
		const result = confirm("Are you sure you want to reset the current department of this user?");
		if(result)
		{
			this.itemService.resetCurrentDepartment(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success("Current Department Reset");
						this.getItems();
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
	}
}